// PaymentDetails.js - Updated with aligned headings
import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Label,
  Button,
  Input,
  CardTitle,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import { getPaymentDetailsById, approveRejectPayment } from "action/payment";
import moment from "moment";
import { toTitleCase } from "components/CommonComponents/CommonFunctions";

function PaymentDetails() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [showAlternative, setShowAlternative] = useState({
    isLoading: true,
    error: "",
  });
  const [pageNum, setPageNum] = useState(1);
  const [actionLoader, setActionLoader] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Inline approve/reject fields - shown directly on the payment card when PENDING
  const [decisionFields, setDecisionFields] = useState({
    remark: { value: "", error: "" },
    paymentDate: { value: null, error: "" },
  });

  const handleDecisionFieldChange = (name, value) => {
    setDecisionFields((prev) => ({
      ...prev,
      [name]: { ...prev[name], value, error: "" },
    }));
  };

  // Fetch payment details - replace with actual API call
  const fetchPaymentDetails = async (paymentId) => {
    try {
      setShowAlternative({
        isLoading: true,
        error: "",
      });
      const response = await getPaymentDetailsById(paymentId);

      if (response?.code === 200) {
        const apiData = response.data;

        // Map API response to our data structure
        const mappedData = {
          id: apiData.id,
          paidByName: apiData.paidByName || "-",
          paidByEmail: apiData.paidByEmail || "-",
          paidByMobile: apiData.paidByMobile || "-",
          courseName: apiData.courseName || "-",
          batchName: apiData.batchName || "-",
          requestedDate: apiData.requestedDate || "-",
          paymentDate: apiData.paymentDate || null,
          paymentStatus: apiData.paymentStatus || "PENDING",
          totalAmount: apiData.totalAmount || 0,
          discountAmount: apiData.discountAmount || 0,
          afterDiscountAmount: apiData.afterDiscountAmount || 0,
          cgst: apiData.cgst || 0,
          igst: apiData.igst || 0,
          sgst: apiData.sgst || 0,
          totalGst: apiData.totalGst || 0,
          payableAmount: apiData.payableAmount || 0,
          paymentRemark: apiData.paymentRemark || "",
          traineeCount: apiData.traineeCount || 0,
          paymentType: apiData.paymentType || "-",
          paymentMode: apiData.paymentmode || "-",
          trainees: apiData.trainees || [],
          paymentHistory: apiData.paymentHistory || [],
          createdOn: apiData.createdOn || "-",
          createdBy: apiData.createdBy || "System",
          registrationStartDate: apiData.registrationStartDate || "-",
          registrationEndDate: apiData.registrationEndDate || "-",
          batchStartDate: apiData.BatchStartDate || "-",
          batchEndDate: apiData.BatchEndDate || "-",
          paidByProfilePath: apiData?.paidByProfilePath || "-",
          cgstPercentage: apiData?.cgst_percentage || "",
          sgstPercentage: apiData?.sgst_percentage || "",
          igstPercentage: apiData?.igst_percentage || "",
          totalGstPercentage: apiData?.totalGst_percentage || "",
        };

        setPaymentData(mappedData);
        setData(mappedData);
        setShowAlternative({
          isLoading: false,
          error: "",
        });
      } else {
        setShowAlternative({
          isLoading: false,
          error: response?.message || "Failed to load payment details.",
        });
      }
    } catch (error) {
      setShowAlternative({
        isLoading: false,
        error: error?.message || "Failed to load payment details.",
      });
    }
  };

  useEffect(() => {
    if (!decodedId) return;
    fetchPaymentDetails(decodedId);
  }, [decodedId]);

  const handleApprove = async (remark, paymentDate) => {
    setActionLoader("approve");
    try {
      const bodyData = {
        action: "APPROVE",
        remark,
        paymentDate: moment(paymentDate).format("YYYY-MM-DD")
      }
      const res = await approveRejectPayment(decodedId, bodyData);
      if (res?.code === 200) {
        setActionLoader(null);
        notify("success", res?.message);
        fetchPaymentDetails(decodedId);
      } else {
        setActionLoader(null);
        notify("danger", res?.message);
      }
    } catch (error) {
      setActionLoader(null);
    }
  };

  const handleReject = async (remark) => {
    setActionLoader("reject");
    try {
      const bodyData = {
        action: "REJECT",
        remark,
        paymentDate: moment(new Date()).format("YYYY-MM-DD")
      }
      const res = await approveRejectPayment(decodedId, bodyData);
      if (res?.code === 200) {
        setActionLoader(null);
        notify("success", res?.message);
        fetchPaymentDetails(decodedId);
      } else {
        setActionLoader(null);
        notify("danger", res?.message);
      }
    } catch (error) {
      setActionLoader(null);
    }
  };

  // Validates inline fields then approves directly - no modal
  const handleApproveClick = () => {
    const remarkVal = decisionFields.remark.value.trim();
    const dateVal = decisionFields.paymentDate.value;
    const remarkError = !remarkVal ? "Please enter a remark" : "";
    const dateError = !dateVal ? "Please select a payment date" : "";

    if (remarkError || dateError) {
      setDecisionFields((prev) => ({
        remark: { ...prev.remark, error: remarkError },
        paymentDate: { ...prev.paymentDate, error: dateError },
      }));
      return;
    }
    handleApprove(remarkVal, dateVal);
  };

  // Validates inline fields then rejects directly - no modal
  const handleRejectClick = () => {
    const remarkVal = decisionFields.remark.value.trim();
    const remarkError = !remarkVal ? "Please enter a remark" : "";

    if (remarkError) {
      setDecisionFields((prev) => ({
        ...prev,
        remark: { ...prev.remark, error: remarkError },
      }));
      return;
    }
    handleReject(remarkVal);
  };

  // Format trainees list for table
  const formattedTraineeList =
    paymentData?.trainees?.map((item, index) => ({
      ...item,
      srNo: (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE + index + 1,
    })) || [];

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 80,
      maxWidth: 80,
    },
    {
      Header: "Trainee Name",
      accessor: "fullName",
      minWidth: 170,
      maxWidth: 170,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Mobile",
      accessor: "mobile",
      centerAlign: true,
      minWidth: 135,
      maxWidth: 135,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    }
  ];

  // const getPaymentModeColor = (status) => {
  //   switch (status) {
  //     case "ONLINE":
  //       return "success";
  //     case "OFFLINE":
  //       return "warning";
  //     default:
  //       return "secondary";
  //   }
  // };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "CAPTURED":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case "CAPTURED":
        return "fa-check-circle";
      case "PENDING":
        return "fa-clock";
      case "FAILED":
        return "fa-times-circle";
      default:
        return "fa-circle";
    }
  };

  if (showAlternative.isLoading) {
    return (
      <>
        <AdminHeader />
        <Container fluid className="mt--6">
          <Row>
            <Col md="12">
              <Card className="section-cards">
                <CardBody>
                  <div className="loaderContainer py-6">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (showAlternative.error) {
    return (
      <>
        <AdminHeader />
        <Container fluid className="mt--6">
          <Row>
            <Col md="12">
              <Card className="section-cards">
                <CardBody>
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{showAlternative.error}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }


  const paymentsChips = [
    {
      id: "paymentTypeChip",
      icon: "fas fa-tag",
      value: toTitleCase(data?.paymentType || "-"),
      tooltip: `Payment Type: ${toTitleCase(data?.paymentType || "-")}`,
    },
    {
      id: "tdEmailChip",
      icon: "fas fa-envelope",
      value: data?.paidByEmail,
      tooltip: data?.paidByEmail,
    },
    {
      id: "tdPhoneChip",
      icon: "fas fa-phone",
      value: data?.paidByMobile,
      tooltip: data?.paidByMobile,
    },
    ...(data?.paymentType === "BULK"
      ? [
        {
          id: "traineeCountChip",
          icon: "fas fa-users",
          value: `${data?.traineeCount || 0} Trainees`,
          tooltip: `Total Trainees: ${data?.traineeCount || 0}`,
        },
      ]
      : []),
  ]

  const today = new Date();
  const isPending = data?.paymentStatus === "PENDING";
  // const isCaptured = data?.paymentStatus === "CAPTURED";
  // const isFailed = data?.paymentStatus === "FAILED";

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <DetailsHeader
          onBack={() =>
            navigate(
              role === "SUPER_ADMIN" ? "/super-admin/payments" : "/admin/payments"
            )
          }
          backTooltip="Back to Payments"
          showProfile={true}
          profileImage={data?.paidByProfilePath}
          onProfileClick={
            data?.paidByProfilePath ? () => setShowImageModal(true) : undefined
          }
          // defaultImage={data?.paidByProfilePath}
          title={data?.paidByName || "-"}
          status={data?.paymentMode}
          showEdit={false}
          chips={paymentsChips}
        />

        <Row className="mt-3">
          <Col md="12">
            <Row className="g-3">
              {/* LEFT COLUMN - 75% width: Course Information + Enrolled Trainees */}
              <Col md="8" className="d-flex flex-column">
                {/* Course Information Card */}
                <Card className="section-cards flex-grow-1">
                  <CardHeader className="section-header">
                    <CardTitle tag="h4" className="mb-0 text-default section-title">
                      Course Information
                    </CardTitle>
                  </CardHeader>

                  <CardBody>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Course Name</Label>
                          <div className="font-weight-medium">{data?.courseName || "-"}</div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Batch Name</Label>
                          <div className="font-weight-medium">{data?.batchName || "-"}</div>
                        </div>
                      </Col>


                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Registration Start Date</Label>
                          <div>{data?.registrationStartDate || "-"}</div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Registration End Date</Label>
                          <div>{data?.registrationEndDate || "-"}</div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Batch Start Date</Label>
                          <div>{data?.batchStartDate || "-"}</div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="mb-3">
                          <Label className="small-label text-muted mb-1">Batch End Date</Label>
                          <div>{data?.batchEndDate || "-"}</div>
                        </div>
                      </Col>

                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* RIGHT COLUMN - 25% width: sticky Payment Summary card */}
              <Col md="4" className="d-flex flex-column">
                <Card
                  className="section-cards payment-summary-card flex-grow-1"
                >
                  <CardHeader className="section-header d-flex align-items-center justify-content-between">
                    <CardTitle
                      tag="h4"
                      className="mb-0 text-default section-title"
                    >
                      Payment Information
                    </CardTitle>

                    <Badge
                      color={getPaymentStatusColor(data?.paymentStatus)}
                      pill
                      className="px-3 py-2 payment-details-badge"
                    >
                      <i
                        className={`fas ${getPaymentStatusIcon(
                          data?.paymentStatus
                        )} mr-1`}
                      />
                      {data?.paymentStatus === "CAPTURED"
                        ? "RECEIVED"
                        : data?.paymentStatus === "FAILED" ? "REJECTED" : "PENDING"}
                    </Badge>
                  </CardHeader>

                  <CardBody>

                    {/* DETAIL ROWS */}
                    <div className="payment-detail-list text-left pt-3 mb-1">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted payment-details-card-heading">
                          Total Amount
                        </span>
                        <span className="payment-detail-list-details-items-text">
                          &#8377;{(data?.totalAmount || 0).toLocaleString("en-IN")}
                        </span>
                      </div>

                      {data?.discountAmount > 0 && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted payment-details-card-heading">
                            Discount
                          </span>
                          <span className="payment-amount-text">
                            -&#8377;{(data?.discountAmount || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {data?.afterDiscountAmount > 0 && data?.discountAmount > 0 && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted payment-details-card-heading">
                            After Discount
                          </span>
                          <span className="payment-detail-list-details-items-text">
                            &#8377;{(data?.afterDiscountAmount || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted payment-details-card-heading">
                          Total GST ({data?.totalGstPercentage}%)
                        </span>
                        <span className="payment-detail-list-details-items-text">
                          &#8377;{(data?.totalGst || 0).toLocaleString("en-IN")}
                        </span>
                      </div>

                      {data?.cgst > 0 && (
                        <div className="d-flex justify-content-between mb-1 payment-details-text-div">
                          <span className="text-muted payment-details-card-heading">
                            CGST ({data?.cgstPercentage}%)
                          </span>
                          <span className="payment-details-tax-heading">
                            &#8377;{(data?.cgst || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {data?.sgst > 0 && (
                        <div className="d-flex justify-content-between mb-1 payment-details-text-div">
                          <span className="text-muted payment-details-card-heading">
                            SGST ({data?.sgstPercentage}%)
                          </span>
                          <span className="payment-details-tax-heading">
                            &#8377;{(data?.sgst || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {data?.igst > 0 && (
                        <div className="d-flex justify-content-between mb-1 payment-details-text-div">
                          <span className="text-muted payment-details-card-heading">
                            IGST ({data?.igstPercentage}%)
                          </span>
                          <span className="payment-details-tax-heading">
                            &#8377;{(data?.igst || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      <div
                        className="d-flex justify-content-between mb-1 payment-modal-payable-amount"
                      >
                        <span
                          className="text-muted payment-details-payable-amount-heading"
                        >
                          Payable Amount
                        </span>
                        <span
                          className="payment-amount-text"
                        >
                          &#8377;{(data?.payableAmount || 0).toLocaleString("en-IN")}
                        </span>
                      </div>

                      <div
                        className="d-flex justify-content-between mb-1"
                      >
                        <span
                          className="text-muted payment-details-payable-amount-heading"
                        >
                          Paid Amount
                        </span>
                        <span
                          className="payment-amount-text"
                        >
                          &#8377;{data?.paymentDate ? (data?.payableAmount || 0).toLocaleString("en-IN") : 0}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted payment-details-payable-amount-heading">
                          Requested Date
                        </span>
                        <span className="payment-detail-list-details-items-text">
                          {data?.requestedDate || "-"}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted payment-details-payable-amount-heading">
                          Payment Date
                        </span>
                        <span className="payment-detail-list-details-items-text">
                          {data?.paymentDate ?
                            data.paymentDate
                            : "-"}
                        </span>
                      </div>
                    </div>

                    {/* INLINE DECISION FIELDS + ACTIONS - only for PENDING */}
                    {isPending && (
                      <>
                        {/* PAYMENT DATE */}
                        <div className="text-left mb-3">
                          <Label className="font-weight-bold text-sm">
                            <i className="fas fa-calendar-alt mr-1" />
                            Payment Date
                          </Label>
                          <div className="payment-date-container">
                            <DatePicker
                              onChangeRaw={(e) => e.preventDefault()}
                              selected={decisionFields.paymentDate.value}
                              onChange={(date) => handleDecisionFieldChange("paymentDate", date)}
                              placeholderText="Select payment date"
                              className={`form-control form-control-sm payment-date-input ${decisionFields.paymentDate.error ? "is-invalid" : ""
                                }`}
                              dateFormat="dd-MMM-yyyy"
                              showMonthDropdown
                              showYearDropdown
                              scrollableYearDropdown
                              calendarClassName="custom-calendar"
                              yearDropdownItemNumber={100}
                              maxDate={today}
                              minDate={new Date(2020, 0, 1)}
                              // isClearable
                              required
                              popperPlacement="bottom-start"
                            />

                            {decisionFields.paymentDate.error && (
                              <div className="invalid-feedback d-block payment-details-card-heading">
                                {decisionFields.paymentDate.error}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* REMARK */}
                        <div className="text-left mb-3">
                          <Label className="font-weight-bold text-sm">
                            <i className="fas fa-comment mr-1" />
                            Remark <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="textarea"
                            rows="2"
                            placeholder="Add a remark for this decision"
                            value={decisionFields.remark.value}
                            onChange={(e) => handleDecisionFieldChange("remark", e.target.value)}
                            maxLength={250}
                            className={`form-control-sm ${decisionFields.remark.error ? "is-invalid" : ""} payment-details-remark-textarea`}
                          />
                          <div className="d-flex justify-content-between mt-1">
                            {decisionFields.remark.error ? (
                              <div className="invalid-feedback d-block payment-details-card-heading">
                                {decisionFields.remark.error}
                              </div>
                            ) : (
                              <small className="text-muted payment-details-card-small-headings">
                                {decisionFields.remark.value.length}/250 characters
                              </small>
                            )}
                          </div>
                        </div>

                        {/* ACTIONS */}
                        <div className="d-flex gap-2">
                          <Button
                            color="danger"
                            className="flex-fill payment-details-approve-reject-btn"
                            onClick={handleRejectClick}
                            disabled={actionLoader === "approve"}
                            size="sm"
                          >
                            {actionLoader === "reject" ? (
                              <i className="fa fa-spinner fa-spin" />
                            ) : (
                              <>
                                <i className="fas fa-times mr-1" />
                                Reject
                              </>
                            )}
                          </Button>
                          <Button
                            color="success"
                            className="flex-fill payment-details-approve-reject-btn"
                            onClick={handleApproveClick}
                            disabled={actionLoader === "reject"}
                            size="sm"
                          >
                            {actionLoader === "approve" ? (
                              <i className="fa fa-spinner fa-spin text-white" />
                            ) : (
                              <>
                                <i className="fas fa-check mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}

                    {/* {isCaptured && (
                        <div className="text-center mt-2">
                          <Badge
                            color="success"
                            pill
                            className="px-4 py-2 payment-details-card-heading"
                           
                          >
                            <i className="fas fa-check-circle mr-1" />
                            Confirmed on {data?.paymentDate || "N/A"}
                          </Badge>
                        </div>
                      )} */}

                    {/* {isFailed && (
                        <div className="text-center mt-2">
                          <Badge
                            color="danger"
                            pill
                            className="px-4 py-2 payment-details-card-heading"
                           
                          >
                            <i className="fas fa-times-circle mr-1" />
                            Payment Failed
                          </Badge>
                        </div>
                      )} */}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ROW 2: Enrolled Trainees and Payment Remark */}
            <Row className="g-3 mt-1">
              <Col md="8">
                {/* Enrolled Trainees List */}
                {data?.paymentType === "BULK" && (
                  <Card className="section-cards h-100">
                    <CardHeader className="section-header">
                      <CardTitle tag="h4" className="mb-0 text-default section-title">
                        Enrolled Trainees
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="mt-2 mb-3 mx-1">
                        {paymentData?.trainees?.length > 0 ? (
                          <span className="total-records">
                            Total Trainees: {paymentData.trainees.length}
                          </span>
                        ) : null}
                      </div>

                      {formattedTraineeList.length === 0 ? (
                        <div className="loaderContainer empty-style">
                          <div className="empty-icon">
                            <i className="fa-solid fa-circle-info"></i>
                          </div>
                          <span>No trainees enrolled for this payment.</span>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <CommonTable
                            columns={columns}
                            data={formattedTraineeList}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}
              </Col>

              <Col md="4">
                {/* PAYMENT REMARK CARD */}
                {data?.paymentRemark && (
                  <Card className="section-cards payment-summary-card">
                    <CardHeader className="section-header">
                      <CardTitle tag="h4" className="mb-0 text-default section-title">
                        Payment Remark
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="mt--2">
                        <span className="payment-detail-list-details-items-text">
                          {data.paymentRemark}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>

          </Col>
        </Row>
      </Container>

      {showImageModal && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div className="image-modal-content">
            <img src={data?.paidByProfilePath} alt="profile-large" />
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentDetails;